import { FileText, FileSpreadsheet, FileCode } from 'lucide-react';
import { ReactComponent as X12Logo } from '../icons/logo_x12.svg';

// import ReactHtmlParser from 'react-html-parser';
import parse from 'html-react-parser';

export const generateRandomId = () => {
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

export const ellipseText = (text, length, isFile = false) => {
    if (!text || text.length <= length) return text;
  
    let name = text;
    let ext = '';
  
    if (isFile) {
      const lastDotIndex = text.lastIndexOf('.');
      if (lastDotIndex > 0 && lastDotIndex < text.length - 1) {
        name = text.slice(0, lastDotIndex);
        ext = text.slice(lastDotIndex);
      }
    }
  
    const dots = '...';
    const availableLength = length - dots.length - ext.length;
    const front = Math.ceil(availableLength / 2);
    const back = Math.floor(availableLength / 2);
  
    const truncated = name.slice(0, front) + dots + name.slice(-back);
  
    return isFile ? truncated + ext : truncated;
  };
  
export function getFileIcon(fileType, size = 20) {
      switch (fileType) {
    case 'xls':
      return {
        icon: <FileSpreadsheet size={size} color="#217346" />, 
        color: '#217346'
      };
    case 'csv':
      return {
        icon: <FileText size={size} color="#FF6B35" />, 
        color: '#FF6B35'
      };
    case 'json':
      return {
        icon: <FileCode size={size} color="#F7DF1E" />, 
        color: '#F7DF1E'
      };
    case 'x12':
      return {
        icon: <X12Logo style={{ width: size, height: size }} />, 
        color: '#2a2a2a'
      };
    case 'jpg':
        return {
            icon: <X12Logo style={{ width: size, height: size }} />, 
            color: '#2a2a2a'
        };
    default:
      return {
        icon: <FileText size={size} />, 
        color: 'var(--text-primary)'
      };
  }
}

export function addCommas(nStr) {
  nStr += '';
  let x = nStr.split('.');
  let x1 = x[0];
  let x2 = x.length > 1 ? '.' + x[1] : '';
  let rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }
  return x1 + x2;
}

export function renderPercentColor(percent, parseReturn = false) {
  let className = '';
  if (percent > 0) className = 'green';
  else if (percent < 0) className = 'red';

  const html = `<span${className ? ` class="${className}"` : ''}>${percent}</span>`;
  return parseReturn && typeof parse === 'function' ? parse(html) : html;
}

export const formatFileSize = (bytes, includeHtml = true, className = "mute", parseReturn = true) => {
  // Helper function to wrap units in the desired tag with optional class
  const wrap = (text) => {
    if (!includeHtml) return text; // No HTML wrapping

    const tag = typeof includeHtml === "string" ? includeHtml : "span";
    const classAttr = className ? ` class="${className}"` : ""; // Only add if className exists
    return `<${tag}${classAttr}>${text}</${tag}>`;
  };

  // Helper function to handle parse() wrapping
  const formatReturn = (str) => (parseReturn ? parse(`${str}`) : str);

  if (bytes < 1024) {
    return formatReturn(`${bytes}${wrap(" bytes")}`);
  } else if (bytes < 1024 * 1024) {
    return formatReturn(`${Math.round(bytes / 1024)}${wrap("kb")}`);
  } else {
    return formatReturn(`${Math.round(bytes / (1024 * 1024))}${wrap("mb")}`);
  }
};

export function formatPercent(passp,plus=true, neutral=false, nonegative=false){
  if(passp && passp !== undefined && ( passp >= 0.01 || passp <= -0.01)){
      passp = Number(passp);
      let color = passp>0 && !neutral ? 'green' : passp < 0 && !neutral ? 'red' : 'neutral';
    let percent = passp < 100 ? addCommas(passp.toFixed(2).toString()) : addCommas(passp.toFixed(0).toString());

      let p = '';
      if(nonegative && passp < .01){
          p = `<span className="small"><</span>0.01</span><span style="margin-left: 0.22rem" className="small">%</span>`;
      }else{
          p = `<span className="${color}${(passp > 0 || passp < 0) && " not-zero"}">${plus && passp > 0 ? '+' : ''}${percent}</span><span style="margin-left: 0.22rem" className="small mute">%</span>`;
      }

      // ▼
    return parse(p);

  }else{
      return parse(`<span className="neutral">0</span><span style="margin-left: 0.22rem" className="small mute">%</span>`);
  }

}

export function dateToTime(d){
  let date = new Date(d);
  return Math.floor(date.getTime() / 1000)
}

export function getTimeDiff(timea,timeb,scale="hours"){
  if(timea == undefined || timeb == undefined){
      return 0;
  }else{
      return (dateToTime(timea) - dateToTime(timeb)) / 3600;
  }
}


export function getTimeDisplay(
  timestamp,
  includeAgo = true,
  includeHtml = true,
  className = "mute",
  parseReturn = true
) {
  const minute = 60;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day;
  const year = 365 * day;
  const diff = Math.floor((new Date() - new Date(timestamp)) / 1000);

  // Helper function to wrap units in the desired tag with optional class
  const wrap = (text) => {
    if (!includeHtml) return text; // No HTML wrapping

    const tag = typeof includeHtml === "string" ? includeHtml : "span";
    const classAttr = className ? ` class="${className}"` : ""; // Only add if className exists
    return `<${tag}${classAttr}>${text}</${tag}>`;
  };

  // Helper function to handle parse() wrapping
  const formatReturn = (str) => (parseReturn ? parse(`${str}`) : str);

  if (diff < minute) return formatReturn("now");

  if (diff < hour) {
    const minutes = Math.floor(diff / minute);
    return formatReturn(`${minutes}${wrap("m")}`);
  }

  if (diff < day) {
    const hours = Math.floor(diff / hour);
    const minutes = Math.floor((diff % hour) / minute);
    return formatReturn(
      minutes > 0
        ? `${hours}${wrap("h")} ${minutes}${wrap("m")}`
        : `${hours}${wrap("h")}`
    );
  }

  if (diff < week) {
    const days = Math.floor(diff / day);
    const hours = Math.floor((diff % day) / hour);
    return formatReturn(
      hours > 0
        ? `${days}${wrap("d")} ${hours}${wrap("h")}`
        : `${days}${wrap("d")}`
    );
  }

  if (diff < month) {
    const weeks = Math.floor(diff / week);
    const days = Math.floor((diff % week) / day);
    return formatReturn(
      days > 0
        ? `${weeks}${wrap("w")} ${days}${wrap("d")}`
        : `${weeks}${wrap("w")}`
    );
  }

  if (diff < year) {
    const months = Math.floor(diff / month);
    const weeks = Math.floor((diff % month) / week);
    if (weeks > 0 && weeks < 4) {
      return formatReturn(`${months}${wrap("m")} ${weeks}${wrap("w")}`);
    }
    return formatReturn(
      months < 12
        ? `${months + (weeks > 3 ? 1 : 0)}${wrap("mo")}`
        : `1${wrap("y")}`
    );
  }

  const years = Math.floor(diff / year);
  const months = Math.floor((diff % year) / month);
  return formatReturn(
    months > 0 && months < 12
      ? `${years}${wrap("y")} ${months}${wrap("mo")}`
      : `${years}${wrap("y")}`
  );
}
  
